/**
 * WorkerWithdrawalPage - Full page for processing a single withdrawal
 *
 * States:
 * - initial: [Auto Cascade] [Manual] [Edit] [Reject]
 * - cascade_pending: [Check] (after successful cascade, waiting for confirmation)
 * - paid: [Confirm ✓] (green, after webhook/check success)
 * - manual_mode: Photo upload at top, Cancel at bottom
 * - manual_uploaded: [Confirm ✓] (green)
 */

import { useState, useRef, useCallback } from 'react';
import { ArrowLeft, Upload, Check, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PopupMessage } from '@/components/shared';

// ============================================
// TYPES
// ============================================

export interface WorkerWithdrawal {
  id: string;
  referenceNumber: string;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  amount: number;
  actualPayout: number | null;
  bonusDeducted: number | null;
  editedAmount: number | null;
  cardNumber: string | null;
  cardHolderName: string | null;
  cardBank: string | null;
  status: string;
  providerTransactionId: string | null;
  createdAt: string;
  isLocked: boolean;
  lockedBy: string | null;
}

type PageState =
  | 'initial'           // Shows all 4 buttons
  | 'cascade_pending'   // After cascade success, shows Check button
  | 'paid'              // After check/webhook, shows Confirm
  | 'manual_mode'       // Photo upload mode
  | 'manual_uploaded';  // After photo upload, shows Confirm

export interface WorkerWithdrawalPageProps {
  withdrawal: WorkerWithdrawal;
  onBack: () => void;
  onAutoCascade: () => Promise<{ success: boolean; status?: string }>;
  onCheck: () => Promise<{ success: boolean; status?: string }>;
  onConfirm: () => Promise<void>;
  onManualComplete: (photo: File) => Promise<void>;
  onEdit: (newAmount: number, reason: string) => Promise<void>;
  onReject: (reason: string) => Promise<void>;
}

// ============================================
// COMPONENT
// ============================================

export function WorkerWithdrawalPage({
  withdrawal,
  onBack,
  onAutoCascade,
  onCheck,
  onConfirm,
  onManualComplete,
  onEdit,
  onReject,
}: WorkerWithdrawalPageProps) {
  // Page state - initialize based on withdrawal status and cascade state
  const [pageState, setPageState] = useState<PageState>(() => {
    const status = withdrawal.status.toLowerCase();
    if (status === 'paid' || status === 'completed') return 'paid';
    // If providerTransactionId is set, cascade was started
    if (withdrawal.providerTransactionId) return 'cascade_pending';
    return 'initial';
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  // Popup states
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showRejectPopup, setShowRejectPopup] = useState(false);

  // Manual mode state
  const [uploadedPhoto, setUploadedPhoto] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Edit form state
  const [editAmount, setEditAmount] = useState('');
  const [editReason, setEditReason] = useState('');

  // Reject form state
  const [rejectReason, setRejectReason] = useState('');

  // ============================================
  // HANDLERS
  // ============================================

  const handleAutoCascade = useCallback(async () => {
    setIsLoading(true);
    setLoadingAction('cascade');
    try {
      const result = await onAutoCascade();
      if (result.success) {
        // Cascade initiated successfully, wait for confirmation
        setPageState('cascade_pending');
      }
      // If failed, stay on initial state (error shown via toast)
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  }, [onAutoCascade]);

  const handleCheck = useCallback(async () => {
    setIsLoading(true);
    setLoadingAction('check');
    try {
      const result = await onCheck();
      if (result.success && result.status === 'completed') {
        // Payment confirmed, show confirm button
        setPageState('paid');
      }
      // If still pending, stay on cascade_pending state
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  }, [onCheck]);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    setLoadingAction('confirm');
    try {
      await onConfirm();
      onBack(); // Go back to list after confirmation
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  }, [onConfirm, onBack]);

  const handleManualClick = useCallback(() => {
    setPageState('manual_mode');
  }, []);

  const handleManualCancel = useCallback(() => {
    setUploadedPhoto(null);
    setPageState('initial');
  }, []);

  const handlePhotoSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedPhoto(file);
      setPageState('manual_uploaded');
    }
  }, []);

  const handleManualConfirm = useCallback(async () => {
    if (!uploadedPhoto) return;
    setIsLoading(true);
    setLoadingAction('manual_confirm');
    try {
      await onManualComplete(uploadedPhoto);
      onBack();
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  }, [uploadedPhoto, onManualComplete, onBack]);

  const handleEditSubmit = useCallback(async () => {
    const amount = parseInt(editAmount);
    if (isNaN(amount) || amount <= 0 || editReason.length < 15) return;

    setIsLoading(true);
    setLoadingAction('edit');
    try {
      await onEdit(amount, editReason);
      setShowEditPopup(false);
      setEditAmount('');
      setEditReason('');
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  }, [editAmount, editReason, onEdit]);

  const handleRejectSubmit = useCallback(async () => {
    if (rejectReason.length < 15) return;

    setIsLoading(true);
    setLoadingAction('reject');
    try {
      await onReject(rejectReason);
      onBack();
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  }, [rejectReason, onReject, onBack]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const displayAmount = withdrawal.editedAmount || withdrawal.actualPayout || withdrawal.amount;

  const renderDetailRow = (label: string, value: string | number | null) => {
    if (value === null || value === undefined) return null;
    return (
      <div className="flex justify-between py-2 border-b border-slate-100 last:border-0">
        <span className="text-sm text-slate-500">{label}</span>
        <span className="text-sm font-medium text-slate-800">{value}</span>
      </div>
    );
  };

  const renderInitialButtons = () => (
    <div className="space-y-3">
      {/* Auto Cascade - Primary action */}
      <button
        type="button"
        onClick={handleAutoCascade}
        disabled={isLoading}
        className={cn(
          'w-full py-4 rounded-2xl font-semibold text-base',
          'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white',
          'shadow-lg shadow-emerald-500/25',
          'hover:from-emerald-600 hover:to-emerald-700',
          'active:scale-[0.98] transition-all',
          isLoading && 'opacity-50 cursor-not-allowed'
        )}
      >
        {loadingAction === 'cascade' ? (
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        ) : (
          'Auto Cascade'
        )}
      </button>

      {/* Secondary actions row */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={handleManualClick}
          disabled={isLoading}
          className={cn(
            'flex-1 py-3 rounded-xl font-semibold text-sm',
            'bg-sky-500 text-white',
            'hover:bg-sky-600 active:scale-[0.98] transition-all',
            isLoading && 'opacity-50 cursor-not-allowed'
          )}
        >
          Manual
        </button>
        <button
          type="button"
          onClick={() => setShowEditPopup(true)}
          disabled={isLoading}
          className={cn(
            'flex-1 py-3 rounded-xl font-semibold text-sm',
            'bg-amber-500 text-black',
            'hover:bg-amber-600 active:scale-[0.98] transition-all',
            isLoading && 'opacity-50 cursor-not-allowed'
          )}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={() => setShowRejectPopup(true)}
          disabled={isLoading}
          className={cn(
            'flex-1 py-3 rounded-xl font-semibold text-sm',
            'bg-rose-500 text-white',
            'hover:bg-rose-600 active:scale-[0.98] transition-all',
            isLoading && 'opacity-50 cursor-not-allowed'
          )}
        >
          Reject
        </button>
      </div>
    </div>
  );

  const renderCascadePendingButtons = () => (
    <button
      type="button"
      onClick={handleCheck}
      disabled={isLoading}
      className={cn(
        'w-full py-4 rounded-2xl font-semibold text-base',
        'bg-gradient-to-r from-sky-500 to-sky-600 text-white',
        'shadow-lg shadow-sky-500/25',
        'hover:from-sky-600 hover:to-sky-700',
        'active:scale-[0.98] transition-all',
        isLoading && 'opacity-50 cursor-not-allowed'
      )}
    >
      {loadingAction === 'check' ? (
        <Loader2 className="w-5 h-5 animate-spin mx-auto" />
      ) : (
        'Check Status'
      )}
    </button>
  );

  const renderPaidButtons = () => (
    <button
      type="button"
      onClick={handleConfirm}
      disabled={isLoading}
      className={cn(
        'w-full py-4 rounded-2xl font-semibold text-base',
        'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white',
        'shadow-lg shadow-emerald-500/25',
        'hover:from-emerald-600 hover:to-emerald-700',
        'active:scale-[0.98] transition-all',
        'flex items-center justify-center gap-2',
        isLoading && 'opacity-50 cursor-not-allowed'
      )}
    >
      {loadingAction === 'confirm' ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <>
          <Check className="w-5 h-5" />
          Confirm
        </>
      )}
    </button>
  );

  const renderManualMode = () => (
    <div className="space-y-4">
      {/* Photo Upload - Top */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Upload Payment Proof</h3>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoSelect}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'w-full py-6 rounded-xl',
            'border-2 border-dashed border-slate-300',
            'bg-slate-50 text-slate-500',
            'hover:border-sky-400 hover:bg-sky-50 hover:text-sky-600',
            'flex flex-col items-center gap-2',
            'transition-all'
          )}
        >
          <Upload className="w-8 h-8" />
          <span className="text-sm font-medium">Tap to upload photo</span>
        </button>
      </div>

      {/* Separator line */}
      <div className="h-px bg-slate-200" />

      {/* Cancel - Bottom */}
      <button
        type="button"
        onClick={handleManualCancel}
        className={cn(
          'w-full py-3 rounded-xl font-semibold text-sm',
          'bg-slate-100 text-slate-700',
          'hover:bg-slate-200 active:scale-[0.98] transition-all'
        )}
      >
        Cancel
      </button>
    </div>
  );

  const renderManualUploadedButtons = () => (
    <div className="space-y-4">
      {/* Show uploaded photo info */}
      <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
            <Check className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-emerald-800 truncate">
              {uploadedPhoto?.name}
            </p>
            <p className="text-xs text-emerald-600">Photo ready for upload</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setUploadedPhoto(null);
              setPageState('manual_mode');
            }}
            className="p-1.5 rounded-full text-emerald-600 hover:bg-emerald-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Confirm button */}
      <button
        type="button"
        onClick={handleManualConfirm}
        disabled={isLoading}
        className={cn(
          'w-full py-4 rounded-2xl font-semibold text-base',
          'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white',
          'shadow-lg shadow-emerald-500/25',
          'hover:from-emerald-600 hover:to-emerald-700',
          'active:scale-[0.98] transition-all',
          'flex items-center justify-center gap-2',
          isLoading && 'opacity-50 cursor-not-allowed'
        )}
      >
        {loadingAction === 'manual_confirm' ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <Check className="w-5 h-5" />
            Confirm
          </>
        )}
      </button>
    </div>
  );

  const renderActionSection = () => {
    switch (pageState) {
      case 'initial':
        return renderInitialButtons();
      case 'cascade_pending':
        return renderCascadePendingButtons();
      case 'paid':
        return renderPaidButtons();
      case 'manual_mode':
        return renderManualMode();
      case 'manual_uploaded':
        return renderManualUploadedButtons();
    }
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-slate-800">{withdrawal.referenceNumber}</h1>
          <p className="text-xs text-slate-500">
            {pageState === 'cascade_pending' && 'Waiting for payment confirmation'}
            {pageState === 'paid' && 'Ready to confirm'}
            {pageState === 'manual_mode' && 'Upload payment proof'}
            {pageState === 'manual_uploaded' && 'Ready to confirm'}
            {pageState === 'initial' && 'Process withdrawal'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Amount Card */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-5 text-center">
          <p className="text-slate-400 text-sm mb-1">Amount</p>
          <p className="text-3xl font-bold text-white">{displayAmount} AZN</p>
          {withdrawal.bonusDeducted && withdrawal.bonusDeducted > 0 && (
            <p className="text-emerald-400 text-sm mt-1">
              Bonus deducted: {withdrawal.bonusDeducted} AZN
            </p>
          )}
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Details</h3>
          <div className="space-y-0">
            {renderDetailRow('User', withdrawal.firstName || withdrawal.username || `ID: ${withdrawal.telegramId}`)}
            {renderDetailRow('Telegram ID', withdrawal.telegramId)}
            {renderDetailRow('Card Number', withdrawal.cardNumber)}
            {withdrawal.cardHolderName && renderDetailRow('Card Holder', withdrawal.cardHolderName)}
            {withdrawal.cardBank && renderDetailRow('Bank', withdrawal.cardBank)}
            {renderDetailRow('Original Amount', `${withdrawal.amount} AZN`)}
            {withdrawal.editedAmount && withdrawal.editedAmount !== withdrawal.amount &&
              renderDetailRow('Edited Amount', `${withdrawal.editedAmount} AZN`)}
            {renderDetailRow('Status', withdrawal.status.toUpperCase())}
            {renderDetailRow('Created', new Date(withdrawal.createdAt).toLocaleString())}
          </div>
        </div>

        {/* Action Section */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Actions</h3>
          {renderActionSection()}
        </div>
      </div>

      {/* Edit Popup */}
      <PopupMessage
        isOpen={showEditPopup}
        onClose={() => {
          setShowEditPopup(false);
          setEditAmount('');
          setEditReason('');
        }}
        title="Edit Amount"
        message={`Current amount: ${displayAmount} AZN. Enter the new amount and provide a reason for the change.`}
        declineLabel="Cancel"
        proceedLabel="Save"
        proceedVariant="warning"
        isLoading={loadingAction === 'edit'}
        proceedDisabled={!editAmount || parseInt(editAmount) <= 0 || editReason.length < 15}
        onDecline={() => {
          setShowEditPopup(false);
          setEditAmount('');
          setEditReason('');
        }}
        onProceed={handleEditSubmit}
        customContent={
          <div className="mt-4 space-y-3">
            {/* Amount input */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">New Amount</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={editAmount}
                  onChange={(e) => {
                    // Only allow numbers
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setEditAmount(val);
                  }}
                  placeholder="0"
                  className={cn(
                    'w-full pl-4 pr-16 py-3 rounded-xl',
                    'bg-slate-50 border border-slate-200',
                    'text-lg font-semibold text-slate-800 placeholder:text-slate-300',
                    'focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent',
                    'text-right'
                  )}
                />
                <span className="absolute right-4 text-sm font-medium text-slate-500">AZN</span>
              </div>
            </div>
            {/* Reason input */}
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
              <textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="Explain why you are changing the amount..."
                rows={3}
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-50 border border-slate-200',
                  'text-sm text-slate-700 placeholder:text-slate-400',
                  'focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent',
                  'resize-none'
                )}
              />
              <p className={cn(
                'mt-1 text-xs',
                editReason.length >= 15 ? 'text-emerald-600' : 'text-slate-400'
              )}>
                {editReason.length}/15 characters
                {editReason.length >= 15 && <Check className="inline w-3 h-3 ml-1" />}
              </p>
            </div>
          </div>
        }
      />

      {/* Reject Popup */}
      <PopupMessage
        isOpen={showRejectPopup}
        onClose={() => {
          setShowRejectPopup(false);
          setRejectReason('');
        }}
        title="Reject Withdrawal"
        message="WARNING: This will permanently reject the withdrawal and confiscate the funds. This action cannot be undone."
        declineLabel="Cancel"
        proceedLabel="Reject"
        proceedVariant="danger"
        isLoading={loadingAction === 'reject'}
        proceedDisabled={rejectReason.length < 15}
        onDecline={() => {
          setShowRejectPopup(false);
          setRejectReason('');
        }}
        onProceed={handleRejectSubmit}
        customContent={
          <div className="mt-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">Reason for rejection</label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Explain why you are rejecting this withdrawal..."
              rows={3}
              className={cn(
                'w-full px-4 py-3 rounded-xl',
                'bg-slate-50 border border-slate-200',
                'text-sm text-slate-700 placeholder:text-slate-400',
                'focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-transparent',
                'resize-none'
              )}
            />
            <p className={cn(
              'mt-1 text-xs',
              rejectReason.length >= 15 ? 'text-emerald-600' : 'text-slate-400'
            )}>
              {rejectReason.length}/15 characters
              {rejectReason.length >= 15 && <Check className="inline w-3 h-3 ml-1" />}
            </p>
          </div>
        }
      />
    </div>
  );
}
